/**
 * useFileSearch — streaming search across CSV file rows for the entity preview.
 *
 * Accepts a File (or Blob) and a query string. When the query changes (debounced),
 * it streams the file through PapaParse in a Web Worker, testing each row for a
 * case-insensitive substring match across all columns. Returns the first N matching
 * rows without loading the entire file into memory.
 *
 * For XLSX files, the caller should convert to a CSV Blob at upload time and pass
 * that here — PapaParse only handles CSV.
 */

import Papa from 'papaparse';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Maximum matching rows to collect before stopping the search. */
const MAX_RESULTS = 20;

/** Debounce delay in ms before triggering a search. */
const DEBOUNCE_MS = 300;

export interface FileSearchResult {
  /** Matching rows as header-keyed objects. */
  results: Record<string, string>[];
  /** Whether a search is currently in progress. */
  isSearching: boolean;
  /** Total matches found (capped at MAX_RESULTS). */
  matchCount: number;
  /** The query that produced the current results (empty = showing sample rows). */
  activeQuery: string;
}

/**
 * Stream-search a CSV file for rows matching a query string.
 *
 * @param csvFile  A File or Blob containing CSV data (pass undefined when unavailable).
 * @param headers  Column headers for the file (used to build row objects).
 * @param query    The search string entered by the user.
 */
export function useFileSearch(
  csvFile: File | Blob | undefined,
  headers: string[],
  query: string
): FileSearchResult {
  const [results, setResults] = useState<Record<string, string>[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [activeQuery, setActiveQuery] = useState('');

  // Ref to track the latest debounce timer so we can cancel on unmount or
  // new query arrival.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref to abort an in-flight PapaParse stream when a new query arrives.
  // PapaParse's step callback can call `parser.abort()` to stop early.
  const abortRef = useRef(false);

  const runSearch = useCallback(
    (searchQuery: string) => {
      if (!csvFile || !searchQuery.trim()) {
        setResults([]);
        setMatchCount(0);
        setActiveQuery('');
        setIsSearching(false);
        return;
      }

      // Abort any in-flight parse.
      abortRef.current = true;

      setIsSearching(true);
      const q = searchQuery.toLowerCase().trim();
      const matches: Record<string, string>[] = [];
      let isFirstRow = true;
      let fileHeaders: string[] = [];

      // Reset abort flag for this run.
      abortRef.current = false;

      Papa.parse(csvFile as File, {
        // worker: false because we need access to the parser object in `step`
        // to call abort(). Web Workers don't expose the parser instance.
        // The streaming approach still avoids loading the full file into memory —
        // PapaParse reads chunks and yields one row at a time.
        worker: false,
        skipEmptyLines: true,
        step: (result: { data: unknown }, parser: { abort: () => void }) => {
          if (abortRef.current) {
            parser.abort();
            return;
          }

          const row = result.data as string[];

          // First row is the header.
          if (isFirstRow) {
            isFirstRow = false;
            fileHeaders = row;
            return;
          }

          // Test every cell for a substring match.
          const isMatch = row.some(cell =>
            String(cell ?? '')
              .toLowerCase()
              .includes(q)
          );

          if (isMatch) {
            // Convert array row to header-keyed object.
            const obj: Record<string, string> = {};
            const hdrs = fileHeaders.length > 0 ? fileHeaders : headers;
            hdrs.forEach((header, idx) => {
              obj[header] = row[idx] ?? '';
            });
            matches.push(obj);

            if (matches.length >= MAX_RESULTS) {
              parser.abort();
            }
          }
        },
        complete: () => {
          if (!abortRef.current) {
            setResults(matches);
            setMatchCount(matches.length);
            setActiveQuery(searchQuery);
            setIsSearching(false);
          }
        },
        error: () => {
          if (!abortRef.current) {
            setResults([]);
            setMatchCount(0);
            setActiveQuery(searchQuery);
            setIsSearching(false);
          }
        },
      });
    },
    [csvFile, headers]
  );

  // Debounce the query and trigger search.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const trimmed = query.trim();
    if (!trimmed) {
      // Clear results immediately when query is empty.
      abortRef.current = true;
      setResults([]);
      setMatchCount(0);
      setActiveQuery('');
      setIsSearching(false);
      return;
    }

    timerRef.current = setTimeout(() => {
      runSearch(trimmed);
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [query, runSearch]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { results, isSearching, matchCount, activeQuery };
}
